import { useFormField } from '@components/form';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconArrowRight } from '@components/icons/arrow-right';
import { IconDate } from '@components/icons/date';
import { IconHourglass } from '@components/icons/hourglass';
import { Separator } from '@components/separator';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { State, StateGroup } from '@components/state-selector/state-group';
import { Text } from '@components/text';
import { VariableValue } from '@components/text-editors/addons/field-mapping';
import { useScopedTranslate } from '@locale/translate';
import { createWeeks } from '@utils/calculateWeek';
import { formatTime } from '@utils/strings';

import { useJourneyAction, useUpdateJourneyAction } from 'api/journey-actions/hooks';
import { BASE_ACTION_TYPE, JOURNEY_ACTION_TYPE, JourneyActionValuesTypes } from 'api/types';
import { showActionSidebar } from 'ui/journey-builder/action-sidebar';
import { useNodes } from 'ui/journey-builder/hooks/use-nodes';
import {
  ActionFooter,
  ActionHeader,
  ActionSidebarLoading,
  SidebarProps,
  createActionForm,
  useCreateActionFormStore,
  useVariableGroups,
} from 'ui/journey-builder/node-types/common';

import { FormWithUnsavedChangesPrompt } from '../../journey-builder/unsaved-changes-prompt';
import { ActionTimeSelector, WaitForDateStateGroup } from '../common';
import { getFieldMappingId } from '../helpers';
import { ActionTimeWithTimezoneTooltip } from '../timezone';
type ActionType = 'wait_until_relative_date';

type WaitUntilDateData = {
  daysBefore: string;
  daysAfter: string;
  hour: string;
  minute: string;
  referenceDate: VariableValue;
  dateOffset: DateOffsetTypes;
};

type DateOffsetTypes = 'on-date' | 'before-date' | 'after-date';
type DateOffsetStateItem = State & { id: DateOffsetTypes };

const Form = createActionForm<WaitUntilDateData>();

export const SidebarContent = (props: SidebarProps) => {
  const { journeyActionId } = props;
  const { data, isLoading } = useJourneyAction<ActionType>(journeyActionId);
  const { updateSelectedNode } = useNodes();

  const [offset] = data?.timezone?.split(' ') ?? [''];
  const values = data?.values ?? {};
  const formStore = useCreateActionFormStore();
  const isDirty = formStore.useIsDirty();
  const dateOffset = formStore.useFieldValue('dateOffset') as DateOffsetTypes;
  const daysBefore = formStore.useFieldValue('daysBefore') as string;
  const daysAfter = formStore.useFieldValue('daysAfter') as string;
  const hour = formStore.useFieldValue('hour') as string;
  const minute = formStore.useFieldValue('minute') as string;
  const defaultHour = 9;
  const defaultMinute = 0;
  const actionQuery = useJourneyAction(journeyActionId);
  const variableGroups = useVariableGroups({
    actionVariables: actionQuery.data?.actionVariables,
    journeyId: data?.journey ?? '',
    types: ['date'],
  });
  const fieldMappingId = getFieldMappingId(data?.journey ?? '');
  const time = formatTime(hour ?? defaultHour, minute ?? defaultMinute);
  const referenceDate = formStore.useFieldValue('referenceDate') as VariableValue;
  const variables = referenceDate?.variables || {};
  const field = variables[Object.keys(variables)[0]]?.label ?? undefined;

  const { t } = useScopedTranslate(sidebarLocale);
  const editMutation = useUpdateJourneyAction(journeyActionId);

  const handleSave = (data: WaitUntilDateData) =>
    editMutation.mutateAsync({
      isAutomatic: true,
      action: 'wait_until_relative_date',
      values: {
        ...(data.daysBefore && { days: { value: data.daysBefore }, comparator: { value: 'lt' } }),
        ...(data.daysAfter && { days: { value: data.daysAfter }, comparator: { value: 'gt' } }),
        hour: { value: data.hour },
        minute: { value: data.minute },
        referenceDate: data.referenceDate,
      },
      description: null,
    });

  if (isLoading || !data) {
    return (
      <ActionSidebarLoading
        title={t('waitforDate')}
        icon={IconHourglass}
        iconColor="orange100"
        {...props}
      />
    );
  }

  return (
    <Sidebar>
      <ActionHeader
        title={t('waitforDate')}
        icon={IconHourglass}
        iconColor="orange100"
        {...props}
      />
      <Sidebar.Content>
        <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
          <Stack space={4}>
            <Stack space={8}>
              <Stack space={4} orientation="vertical">
                <WaitForDateStateGroup
                  selectedStateId="wait_until_relative_date"
                  onStateSelected={(state) => {
                    formStore.actions.resetStore();
                    window.requestAnimationFrame(() => {
                      showActionSidebar({
                        type: state.id as JOURNEY_ACTION_TYPE,
                        id: journeyActionId,
                      });
                      updateSelectedNode({ type: state.id as BASE_ACTION_TYPE });
                    });
                  }}
                />
              </Stack>
              <Stack space={6}>
                <Text font="regular14" text={t('configuration')} />
                <Separator />

                <Stack space={4}>
                  <Form.FieldMappingField
                    id="referenceDate"
                    label={t('referenceDate')}
                    plaintext
                    singleLine
                    variableGroups={variableGroups}
                    defaultValue={values.referenceDate}
                    validators={['required']}
                    info={t('referenceDateInfo')}
                    fieldMappingId={fieldMappingId}
                  />
                  <ReferenceDate values={values} />
                  {dateOffset === 'before-date' && (
                    <Stack space={4}>
                      <Form.TextField
                        defaultValue={values.days?.value ?? '0'}
                        id="daysBefore"
                        label={t('daysBefore')}
                        validators={['number']}
                      />
                      <ActionTimeSelector
                        defaultHour={values.hour?.value ?? defaultHour.toString()}
                        defaultMinute={values.minute?.value ?? defaultMinute.toString()}
                      />
                      <ActionTimeWithTimezoneTooltip
                        actionText={`${t('beforeInfotextOne')} ${createWeeks(
                          Number(daysBefore),
                          t
                        )} ${t('beforeInfotextTwo', { field, time })}`}
                        hour={hour}
                        minute={minute}
                        journeyTimezoneOffset={offset}
                      />
                    </Stack>
                  )}
                  {dateOffset === 'on-date' && (
                    <Stack space={4}>
                      <ActionTimeSelector
                        defaultHour={values.hour?.value ?? defaultHour.toString()}
                        defaultMinute={values.minute?.value ?? defaultMinute.toString()}
                      />
                      <ActionTimeWithTimezoneTooltip
                        actionText={t('onDateInfoText', { field, time })}
                        hour={hour}
                        minute={minute}
                        journeyTimezoneOffset={offset}
                      />
                    </Stack>
                  )}
                  {dateOffset === 'after-date' && (
                    <Stack space={4}>
                      <Form.TextField
                        defaultValue={values.days?.value ?? '0'}
                        id="daysAfter"
                        label={t('daysAfter')}
                        validators={['number', 'required']}
                      />
                      <ActionTimeSelector
                        defaultHour={values.hour?.value ?? defaultHour.toString()}
                        defaultMinute={values.minute?.value ?? defaultMinute.toString()}
                      />
                      <ActionTimeWithTimezoneTooltip
                        actionText={`${t('afterInfotextOne')} ${createWeeks(
                          Number(daysAfter),
                          t
                        )} ${t('afterInfotextTwo', { field, time })}`}
                        hour={hour}
                        minute={minute}
                        journeyTimezoneOffset={offset}
                      />
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </Stack>
            <ActionFooter
              action={data}
              isDisabled={!isDirty || editMutation.isLoading}
              form={Form}
            />
          </Stack>
        </FormWithUnsavedChangesPrompt>
      </Sidebar.Content>
    </Sidebar>
  );
};

export const ReferenceDate = ({ values }: { values: JourneyActionValuesTypes[ActionType] }) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const initialOffsetState =
    values?.comparator?.value === 'lt'
      ? 'before-date'
      : values?.comparator?.value === 'gt'
      ? 'after-date'
      : 'on-date';

  const dateOffsetProps = useFormField({
    id: 'dateOffset',
    label: 'Date offset',
    defaultValue: initialOffsetState,
  });
  return (
    <StateGroup
      orientation="horizontal"
      selectedStateId={initialOffsetState}
      onStateSelected={(state) => {
        dateOffsetProps.onChange(state.id);
      }}
      stateItems={
        [
          {
            id: 'before-date',
            orientation: 'vertical',
            icon: <IconArrowLeft />,
            label: t('beforeDate'),
          },
          {
            id: 'on-date',
            orientation: 'vertical',
            icon: <IconDate />,
            label: t('stateSelectDate'),
          },
          {
            id: 'after-date',
            orientation: 'vertical',
            icon: <IconArrowRight />,
            label: t('afterDate'),
          },
        ] as DateOffsetStateItem[]
      }
    />
  );
};

const sidebarLocale = {
  en: {
    waitforDate: 'Wait until # date',
    referenceDate: '* Reference Date',
    configuration: 'Select a date based on travellers journey property',
    stateSelectDate: 'On date',
    daysBefore: '* Days before',
    beforeInfotextTwo: 'before {field} at {time}',
    beforeInfotextOne: 'Triggers',
    beforeDate: 'Before date',
    weeks: 'weeks',
    days: 'days',
    onDateInfoText: 'Triggers on {field} at {time}',
    afterInfotextTwo: 'after {field} at {time}',
    afterInfotextOne: 'Triggers',
    hour: '* Hour',
    minute: '* Minute',
    daysAfter: '* Days after',
    afterDate: 'After date',
    referenceDateInfo: 'Date that you want to use as a reference point',
    timezoneTooltip: 'Action • {action}\nYour computer • {computer}',
  },
};
